import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { PFieldAbstractComponent } from '../p-field-abstract';
import { MatDatepicker } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, NativeDateAdapter } from '@angular/material/core';
import { AfterViewInit, Injectable } from '@angular/core';
import { format } from 'date-fns';
import { ButtonTypeEnum } from '@panaya/ui/p-button';

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

@Injectable()
export class AppDateAdapter extends NativeDateAdapter {
    override format(date: Date, displayFormat: any): string {
        const day = date.getDate();
        const month = date.getMonth();
        const year = date.getFullYear();
        if (displayFormat === 'input') {
            return format(date, 'dd-MMM-yyyy');
        }
        if (displayFormat === 'monthYearLabel') {
            return `${months[month]}-${year}`;
        }
        return date.toDateString();
    }
}

export const APP_DATE_FORMATS = {
    display: {
        dateInput: 'input',
        monthYearLabel: 'monthYearLabel'
    }
};

@Component({
    selector: 'p-field-date',
    template: `
        <p-field
            [isMandatory]="isMandatory"
            [label]="label"
            [labelTooltip]="labelTooltip"
            [isReadOnly]="isReadOnly"
            [isDisable]="isDisabled"
            [shouldTransformLabelTextCase]="shouldTransformLabelTextCase"
        >
            <div slot="readMode">
                <mat-form-field class="p-input-text" appearance="fill" [class.p-input-text-readonly]="isReadOnly">
                    <input
                        [attr.automation-id]="'p-field-date_' + label"
                        matInput
                        [value]="value | date: 'dd-MMM-yyy'"
                        class="p-input-text-input"
                        [attr.aria-label]="ariaLabel"
                        readonly
                        [disabled]="isDisabled"
                    />
                </mat-form-field>
            </div>
            <div slot="editMode">
                <mat-form-field class="p-datepicker p-input-text" appearance="fill">
                    <input
                        [attr.automation-id]="'p-field-date_' + label"
                        matInput
                        [value]="value"
                        [min]="min"
                        [max]="max"
                        [matDatepicker]="picker"
                        class="p-input-text-input"
                        [attr.aria-label]="ariaLabel"
                        autocomplete="off"
                        readonly
                        [placeholder]="placeholder | sentenceCase"
                        (click)="openDatepicker()"
                        (keydown.enter)="openDatepicker()"
                        (dateChange)="changeValue($event?.value)"
                        [tabindex]="!isDisabled && !!value ? '0' : '-1'"
                    />

                    <p-button
                        class="p-field-input-icon"
                        [iconBefore]="!isDisabled && !!value ? 'x' : 'calendar'"
                        iconBeforeSize="sm"
                        [buttonType]="buttonType"
                        [class.shown]="isOpened"
                        (keydown.enter)="iconOperation(); $event.preventDefault()"
                        (keydown.arrowDown)="iconOperation()"
                        (buttonClicked)="iconOperation()"
                    ></p-button>

                    <mat-datepicker
                        #picker
                        (closed)="streamClosed()"
                        (opened)="onOpen()"
                        [opened]="autoOpen"
                        panelClass="p-datepicker-panel {{ datePickerPanelClass }}"
                    ></mat-datepicker>
                </mat-form-field>
                <div style="display: none">
                    <div
                        class="datepicker-footer p-paragraph-1"
                        [ngClass]="{ disable: !isCurrentDateInRange() }"
                        #datepickerFooter
                        (click)="selectCurrentDate()"
                    >
                        {{ currentDate | date: 'fullDate' }}
                    </div>
                </div>
            </div>
        </p-field>
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PFieldDateComponent
        },
        {
            provide: DateAdapter,
            useClass: AppDateAdapter
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: APP_DATE_FORMATS
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'pFieldDate',
    styleUrls: ['./p-field-date.component.scss']
})
export class PFieldDateComponent extends PFieldAbstractComponent<Date> implements AfterViewInit {
    @ViewChild('picker') picker: MatDatepicker<Date> = {} as MatDatepicker<Date>;
    @ViewChild('datepickerFooter', { static: false }) datepickerFooter?: ElementRef;

    @Input() label?: string;
    @Input() override ariaLabel: string = '';
    @Input() isMandatory = false;
    @Input() min?: Date;
    @Input() max?: Date;
    @Input() autoOpen = false;
    @Input() placeholder?: string;
    @Input() datePickerPanelClass?: string;

    @Output() dateChange: EventEmitter<any> = new EventEmitter();

    public isOpened = false;
    buttonType = ButtonTypeEnum.IsIcon;
    currentDate = new Date();

    constructor(cd: ChangeDetectorRef) {
        super(cd);
    }

    openDatepicker(): void {
        this.isOpened = true;
        this.picker.open();
    }

    streamClosed(): void {
        this.isOpened = false;
    }

    clearValue() {
        this.changeValue(undefined);
        this.cd.detectChanges();
        return true;
    }

    changeValue(value: Date | undefined) {
        this.markAsTouched();
        if (!this.isReadOnly) {
            this.value = value;
            this._onChange(this.value);
            this.dateChange.emit(this.value);
        }
    }

    selectCurrentDate() {
        if (this.isCurrentDateInRange()) {
            this.changeValue(this.currentDate);
            this.picker.close();
        }
    }

    emptyMinMax(): boolean {
        return !this.min && !this.max;
    }

    betweenMinAndMax(): boolean | undefined {
        return this.min && this.max && this.currentDate >= this.min && this.currentDate <= this.max;
    }

    greaterThanMin(): boolean | undefined {
        return !this.max && this.min && this.currentDate >= this.min;
    }

    lowerThanMax(): boolean | undefined {
        return !this.min && this.max && this.currentDate <= this.max;
    }

    isCurrentDateInRange(): boolean | undefined {
        return this.emptyMinMax() || this.greaterThanMin() || this.lowerThanMax() || this.betweenMinAndMax();
    }

    private appendFooter() {
        const matCalendar = document.getElementsByClassName('mat-calendar')[0] as HTMLElement;
        if (!!matCalendar && this.datepickerFooter) {
            matCalendar.appendChild(this.datepickerFooter.nativeElement);
        }
    }

    onOpen() {
        this.appendFooter();
    }

    ngAfterViewInit(): void {
        if (this.autoOpen) {
            this.onOpen();
        }
    }

    iconOperation() {
        if (!this.isDisabled && !!this.value) {
            this.clearValue();
        } else {
            this.openDatepicker();
        }
    }
}
